import { productNameOf } from '../catalog/product-stock.ts';
import { variantLabel, type OptionIndex } from '../catalog/options.ts';
import type { Product } from '../catalog/product.ts';
import { healthOfLevel, type StockLevel } from './inventory.ts';
import type { StockRow } from './StockMatrix.tsx';

const DETAIL_SEPARATOR = ' · ';

export type GridRow = StockRow & { readonly productId: string };

export const gridRowsOf = (
  products: readonly Product[],
  index: OptionIndex,
  locale: string,
): readonly GridRow[] =>
  products.flatMap((product) => {
    const name = productNameOf(product, locale);
    return product.variants.map((variant) => {
      const options =
        variant.optionValueIds.length === 0 ? null : variantLabel(variant.optionValueIds, index, variant.sku);
      return {
        variantId: variant.id,
        productId: product.id,
        label: name,
        detail: options === null ? variant.sku : [options, variant.sku].join(DETAIL_SEPARATOR),
      };
    });
  });

const levelAt = (levels: readonly StockLevel[], variantId: string, locationId: string) =>
  levels.find((level) => level.variantId === variantId && level.locationId === locationId);

export const needsRestock = (
  row: StockRow,
  levels: readonly StockLevel[],
  locationIds: readonly string[],
): boolean =>
  locationIds.some((locationId) => healthOfLevel(levelAt(levels, row.variantId, locationId)) !== 'ok');

export const levelsShown = (
  levels: readonly StockLevel[],
  rows: readonly StockRow[],
  locationIds: readonly string[],
): readonly StockLevel[] => {
  const variantIds = new Set(rows.map((row) => row.variantId));
  const shownLocations = new Set(locationIds);
  return levels.filter((level) => variantIds.has(level.variantId) && shownLocations.has(level.locationId));
};
